import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_TableOptions,
} from "material-react-table";

interface MaterialTableProps<T extends Record<string, any>> {
  columns: MRT_ColumnDef<T>[];
  data: T[];
  isLoading?: boolean;
  error?: boolean;
  emptyMessage?: string;
  enableSorting?: boolean;
  enableFilters?: boolean;
  enablePagination?: boolean;
  enableRowSelection?: boolean;
  enableColumnOrdering?: boolean;
  enableGlobalFilter?: boolean;
  enableTopToolbar?: boolean;
  enableBottomToolbar?: boolean;
  enableColumnPinning?: boolean;
  enableColumnVisibility?: boolean; // ✅ new prop
  stickyColumns?: {
    left?: string[];
    right?: string[];
  };
  className?: string;
  tableOptions?: Partial<MRT_TableOptions<T>>;
}

export function MaterialTable<T extends Record<string, any>>({
  columns,
  data,
  isLoading = false,
  error = false,
  emptyMessage = "No data found",
  enableSorting = false,
  enableFilters = false,
  enablePagination = false,
  enableRowSelection = false,
  enableColumnOrdering = false,
  enableGlobalFilter = false,
  enableTopToolbar = false,
  enableBottomToolbar = false,
  enableColumnPinning = false,
  enableColumnVisibility = true,
  stickyColumns = { right: ["actions"] },
  className = "",
  tableOptions = {},
}: MaterialTableProps<T>) {
  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection,
    enableColumnOrdering,
    enableGlobalFilter,
    enableFilters,
    enableSorting,
    enablePagination,
    enableTopToolbar,
    enableBottomToolbar,
    enableHiding: enableColumnVisibility, 
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnPinning: enableColumnPinning,

    initialState: {
      density: "compact",
      ...(enableColumnPinning && stickyColumns
        ? { columnPinning: stickyColumns }
        : {}),
    },

    muiTableProps: {
      sx: { fontSize: "12px" },
    },

    muiTableHeadCellProps: {
      sx: {
        fontSize: "12px",
        fontWeight: 500,
        color: "rgb(107, 114, 128)",
        backgroundColor: "rgb(249, 250, 251)",
      },
    },

    muiTableBodyCellProps: {
      sx: {
        fontSize: "12px",
        fontWeight: 400,
        color: "rgb(156, 163, 175)",
      },
    },

    muiTableBodyRowProps: {
      sx: {
        fontSize: "12px",
        fontWeight: 400,
        color: "rgb(156, 163, 175)",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          "& .action-buttons": { opacity: 1 },
        },
        "& .action-buttons": {
          opacity: 0,
          transition: "opacity 0.2s ease-in-out",
        },
      },
    },

    state: {
      isLoading,
      showAlertBanner: false,
      showProgressBars: false,
    },

    renderEmptyRowsFallback: () => (
      <div className="text-center py-6 text-bits-grey-600 text-sm">
        {isLoading
          ? "Loading..."
          : error
          ? "Failed to load data"
          : emptyMessage}
      </div>
    ),

    ...tableOptions,
  });

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
      <MaterialReactTable table={table} />
    </div>
  );
}

export default MaterialTable;
